import { Injectable, signal } from '@angular/core';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
  /** Type-to-confirm: the confirm button stays disabled until the user types this exact string. */
  requireText?: string;
}

export interface InputOptions {
  title: string;
  label: string;
  placeholder?: string;
  maxlength?: number;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface ActiveDialog {
  mode: 'confirm' | 'input';
  title: string;
  message?: string;
  label?: string;
  placeholder?: string;
  maxlength?: number;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'primary' | 'danger';
  requireText?: string;
  /** confirm mode: resolve(true/false). input mode: resolve(value)/resolve(null). */
  resolve: (result: boolean | string | null) => void;
}

/**
 * App-styled replacement for the native confirm()/prompt() dialogs that used
 * to gate destructive and core actions. Promise-based so imperative callers
 * stay a one-line `.then(ok => ...)` away from their old shape. The single
 * DialogHostComponent (mounted once in AppComponent) renders whatever is in
 * `active`; opening a new dialog while one is up cancels the first.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly _active = signal<ActiveDialog | null>(null);
  readonly active = this._active.asReadonly();

  confirm(options: ConfirmOptions): Promise<boolean> {
    this.cancelActive();
    return new Promise<boolean>((resolve) => {
      this._active.set({
        mode: 'confirm',
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel ?? 'Confirm',
        cancelLabel: options.cancelLabel ?? 'Cancel',
        variant: options.variant ?? 'primary',
        requireText: options.requireText,
        resolve: (result) => resolve(result === true),
      });
    });
  }

  promptText(options: InputOptions): Promise<string | null> {
    this.cancelActive();
    return new Promise<string | null>((resolve) => {
      this._active.set({
        mode: 'input',
        title: options.title,
        label: options.label,
        placeholder: options.placeholder,
        maxlength: options.maxlength,
        confirmLabel: options.confirmLabel ?? 'OK',
        cancelLabel: options.cancelLabel ?? 'Cancel',
        variant: 'primary',
        resolve: (result) => resolve(typeof result === 'string' ? result : null),
      });
    });
  }

  /** Called by the host component when the user decides. */
  settle(result: boolean | string | null): void {
    const dialog = this._active();
    if (!dialog) return;
    this._active.set(null);
    dialog.resolve(result);
  }

  private cancelActive(): void {
    const dialog = this._active();
    if (!dialog) return;
    this._active.set(null);
    dialog.resolve(dialog.mode === 'confirm' ? false : null);
  }
}
